/*
 * DEV-118 — Per-variant YouTube videos in the product gallery.
 *
 * Companion to variant-meta-images.js. That script injects each variant's
 * custom.variant_images into the gallery on variant change; this one does the
 * same for each variant's custom.variant_videos (YouTube links), rendering a
 * lightweight facade (thumbnail + play button) that loads the real YouTube
 * iframe only when clicked — so a product with many per-variant videos stays
 * fast (no N iframes on load).
 *
 * It listens to the SAME `variant:change` event and mirrors the image script's
 * injection points: main slider (.js-media-list), thumbnail strip
 * (.js-media-sublist), fullscreen popup (.js-popup-slider), and the
 * variant_grid layout (.product__outer--variant-grid).
 *
 * Video slides are marked .product__media-item--variant-meta-video (a subclass
 * of --variant-meta) so variant-meta-images.js's removeMetaSlides() also clears
 * them, and so gallery filtering treats them as variant-specific meta slides.
 */
(function () {
  'use strict';

  // ── YouTube URL / id handling ──────────────────────────────────────────────
  // Accepts every format the connector might push and returns the 11-char id.
  //   https://www.youtube.com/watch?v=ID   /  ?v=ID&list=...
  //   https://youtu.be/ID
  //   https://www.youtube.com/embed/ID
  //   https://www.youtube.com/shorts/ID
  //   https://www.youtube-nocookie.com/embed/ID
  //   ID (bare 11-char)
  function extractYouTubeId(raw) {
    if (!raw) return null;
    var url = String(raw).trim();
    // Bare id
    if (/^[A-Za-z0-9_-]{11}$/.test(url)) return url;
    var m;
    m = url.match(/[?&]v=([A-Za-z0-9_-]{11})/);            if (m) return m[1];
    m = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);        if (m) return m[1];
    m = url.match(/\/embed\/([A-Za-z0-9_-]{11})/);          if (m) return m[1];
    m = url.match(/\/shorts\/([A-Za-z0-9_-]{11})/);         if (m) return m[1];
    m = url.match(/\/v\/([A-Za-z0-9_-]{11})/);              if (m) return m[1];
    // Last resort: first 11-char token that looks like an id
    m = url.match(/([A-Za-z0-9_-]{11})/);                   if (m) return m[1];
    return null;
  }

  function youTubePoster(id) {
    // hqdefault always exists (unlike maxres). 480x360, 4:3.
    return 'https://i.ytimg.com/vi/' + id + '/hqdefault.jpg';
  }

  function youTubeEmbedSrc(id) {
    return 'https://www.youtube.com/embed/' + id +
           '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
  }

  // ── Read per-variant video data emitted by variant-meta-videos.liquid ──
  function getMetaVideos(sectionId, variantId) {
    var container = document.getElementById('variant-meta-videos-data-' + sectionId);
    if (!container) return [];
    var variantDiv = container.querySelector('[data-variant-id="' + variantId + '"]');
    if (!variantDiv) return [];
    var out = [];
    variantDiv.querySelectorAll('span[data-meta-video]').forEach(function (el) {
      var id = extractYouTubeId(el.getAttribute('data-meta-video'));
      if (!id) return;
      out.push({ id: id, alt: el.getAttribute('data-meta-alt') || '' });
    });
    // De-dupe by id within the variant
    var seen = {};
    return out.filter(function (v) {
      if (seen[v.id]) return false;
      seen[v.id] = true;
      return true;
    });
  }

  function hasAnyVideoData(sectionId) {
    return !!document.querySelector(
      '#variant-meta-videos-data-' + sectionId + ' [data-variant-id] span[data-meta-video]'
    );
  }

  // ── Facade builders ────────────────────────────────────────────────────────
  // A facade = poster image + play button. Clicking swaps in the YouTube iframe.
  function buildFacadeInner(video, poster, extraImgStyle) {
    return (
      '<div class="variant-video-facade" data-yt-id="' + video.id + '" role="button" tabindex="0"' +
           ' aria-label="Play video">' +
        '<img class="variant-video-facade__poster" src="' + poster + '"' +
             ' alt="' + (video.alt || '').replace(/"/g, '&quot;') + '" loading="lazy"' +
             (extraImgStyle ? ' style="' + extraImgStyle + '"' : '') + '>' +
        '<span class="variant-video-facade__play" aria-hidden="true">' +
          '<svg viewBox="0 0 68 48" width="68" height="48">' +
            '<path class="variant-video-facade__play-bg" d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"></path>' +
            '<path d="M45 24 27 14v20" fill="#fff"></path>' +
          '</svg>' +
        '</span>' +
      '</div>'
    );
  }

  // ── Dedicated video lightbox ───────────────────────────────────────────────
  // IMPORTANT: we must NOT play the YouTube iframe inside a swiper slide. The
  // swiper wrapper carries a large CSS transform (e.g. translate(-5000px)), and
  // YouTube's video compositing layer renders BLACK (audio only) inside a
  // transformed ancestor. So every video click opens THIS fixed, top-level
  // overlay — outside any transform — where the video plays correctly.
  var _lightbox = null;
  function getLightbox() {
    if (_lightbox) return _lightbox;
    var lb = document.createElement('div');
    lb.className = 'variant-video-lightbox';
    lb.setAttribute('aria-hidden', 'true');
    lb.innerHTML =
      '<div class="variant-video-lightbox__backdrop"></div>' +
      '<div class="variant-video-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Video">' +
        '<button class="variant-video-lightbox__close" type="button" aria-label="Close">' +
          '<svg viewBox="0 0 24 24" width="24" height="24"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<div class="variant-video-lightbox__frame"></div>' +
      '</div>';
    document.body.appendChild(lb);
    function close() { closeLightbox(); }
    lb.querySelector('.variant-video-lightbox__close').addEventListener('click', close);
    lb.querySelector('.variant-video-lightbox__backdrop').addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lb.classList.contains('is-open')) close();
    });
    _lightbox = lb;
    return lb;
  }

  function openLightbox(id) {
    if (!id) return;
    var lb = getLightbox();
    var frame = lb.querySelector('.variant-video-lightbox__frame');
    var iframe = document.createElement('iframe');
    iframe.className = 'variant-video-lightbox__iframe';
    iframe.setAttribute('src', youTubeEmbedSrc(id));
    iframe.setAttribute('title', 'YouTube video player');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    iframe.setAttribute('allowfullscreen', '');
    frame.innerHTML = '';
    frame.appendChild(iframe);
    lb.classList.add('is-open');
    lb.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeLightbox() {
    if (!_lightbox) return;
    _lightbox.classList.remove('is-open');
    _lightbox.setAttribute('aria-hidden', 'true');
    _lightbox.querySelector('.variant-video-lightbox__frame').innerHTML = ''; // stop playback
    document.documentElement.style.overflow = '';
  }

  // Any element carrying a data-yt-id opens the lightbox.
  var _delegationBound = false;
  function bindFacadeDelegation() {
    if (_delegationBound) return;
    _delegationBound = true;
    function handle(e) {
      var trigger = e.target.closest && e.target.closest('[data-yt-id]');
      if (!trigger) return;
      if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      e.stopPropagation();
      openLightbox(trigger.getAttribute('data-yt-id'));
    }
    document.addEventListener('click', handle, true);
    document.addEventListener('keydown', handle, true);
  }

  // ── Remove previously-injected video slides ────────────────────────────────
  function removeVideoSlides() {
    document.querySelectorAll('.product__media-item--variant-meta-video, ' +
                              '.product__media-subitem--variant-meta-video, ' +
                              '.product-media-modal__item--variant-meta-video')
      .forEach(function (el) { el.remove(); });
  }

  // ── Swiper-gallery injection (slider_previews / slider_bullets / horizontal) ─
  function injectVideosSwiper(sectionId, variantId) {
    var videos = getMetaVideos(sectionId, String(variantId));
    if (!videos.length) return;

    var mainSwiperEl = document.querySelector('.js-media-list');
    var swiperWrapper = mainSwiperEl && mainSwiperEl.querySelector('.swiper-wrapper');
    if (!swiperWrapper) return;

    // Match aspect ratio to an existing (non-meta-video) slide for a consistent box.
    var existingMedia = swiperWrapper.querySelector(
      '.product__media-item:not(.product__media-item--variant-meta-video) .product__media');
    var pb = existingMedia && existingMedia.style.paddingBottom
      ? existingMedia.style.paddingBottom : '100%';

    // ── Main slider ── (append video slides after the last current slide)
    videos.forEach(function (video, i) {
      var mediaId = sectionId + '-metavideo-' + variantId + '-' + i;
      var slide = document.createElement('div');
      slide.className = 'product__media-item swiper-slide product__media-item--variant ' +
        'product__media-item--variant-meta product__media-item--variant-meta-video ' +
        'product__media-item--contain';
      slide.setAttribute('data-media-id', mediaId);
      slide.innerHTML =
        '<div class="product__media media variant-video-media" style="padding-bottom:' + pb + '">' +
          buildFacadeInner(video, youTubePoster(video.id), 'object-fit:cover;') +
        '</div>';
      swiperWrapper.appendChild(slide);
    });
    if (mainSwiperEl.swiper) mainSwiperEl.swiper.update();

    // ── Thumbnail strip ── (poster + ▶ badge; click jumps main slider to it)
    var subSwiperEl = document.querySelector('.js-media-sublist');
    if (subSwiperEl && subSwiperEl.swiper) {
      var subWrapper = subSwiperEl.querySelector('.swiper-wrapper');
      videos.forEach(function (video, i) {
        var mediaId = sectionId + '-metavideo-' + variantId + '-' + i;
        var thumb = document.createElement('div');
        thumb.className = 'product__media-subitem swiper-slide product__media-item--variant ' +
          'product__media-subitem--variant-meta product__media-subitem--variant-meta-video';
        thumb.setAttribute('data-media-sub-id', mediaId);
        thumb.innerHTML =
          '<div class="product__media variant-video-thumb">' +
            '<img src="' + youTubePoster(video.id) + '" alt="' +
              (video.alt || '').replace(/"/g, '&quot;') + '" loading="lazy">' +
            '<span class="variant-video-thumb__badge" aria-hidden="true">' +
              '<svg viewBox="0 0 24 24" width="16" height="16"><path d="M8 5v14l11-7z" fill="#fff"/></svg>' +
            '</span>' +
          '</div>';
        thumb.addEventListener('click', function () {
          if (!mainSwiperEl.swiper) return;
          var target = mainSwiperEl.querySelector('[data-media-id="' + mediaId + '"]');
          var idx = Array.from(mainSwiperEl.swiper.slides).indexOf(target);
          if (idx > -1) mainSwiperEl.swiper.slideTo(idx);
        });
        subWrapper.appendChild(thumb);
      });
      subSwiperEl.swiper.update();
    }

    // ── Popup (fullscreen) slider ──
    injectVideosPopup(sectionId, variantId, videos);
  }

  // ── Popup slides (shared by swiper + grid paths) ──
  function injectVideosPopup(sectionId, variantId, videos) {
    var popupSwiperEl = document.querySelector('.js-popup-slider');
    if (!popupSwiperEl || !popupSwiperEl.swiper) return;
    var popupWrapper = popupSwiperEl.querySelector('.swiper-wrapper');
    if (!popupWrapper) return;

    videos.forEach(function (video, i) {
      var mediaId = sectionId + '-metavideo-' + variantId + '-' + i;
      var slide = document.createElement('div');
      slide.className = 'swiper-slide product-media-modal__item product__media-item--variant ' +
        'product__media-item--variant-meta product-media-modal__item--variant-meta-video';
      slide.setAttribute('data-media-modal-id', mediaId);
      slide.setAttribute('data-media-id', mediaId);
      slide.innerHTML =
        '<div class="variant-video-modal">' +
          buildFacadeInner(video, youTubePoster(video.id), '') +
        '</div>';
      popupWrapper.appendChild(slide);
    });
    popupSwiperEl.swiper.update();
  }

  // ── Variant-grid layout injection ──────────────────────────────────────────
  function injectVideosGrid(sectionId, variantId) {
    var outer = document.querySelector('.product__outer--variant-grid');
    if (!outer) return;
    var list = outer.querySelector('.product__media-list.no-js-hidden');
    if (!list) return;

    var videos = getMetaVideos(sectionId, String(variantId));
    if (!videos.length) return;

    var nativeMedia = list.querySelector(
      '.product__media-item:not([data-variant-hidden]):not(.product__media-item--variant-meta-video) .product__media');
    var pb = nativeMedia && nativeMedia.style.paddingBottom
      ? nativeMedia.style.paddingBottom : '100%';

    videos.forEach(function (video, i) {
      var mediaId = sectionId + '-metavideo-' + variantId + '-' + i;
      var cell = document.createElement('div');
      cell.className = 'product__media-item product__media-item--variant ' +
        'product__media-item--variant-meta product__media-item--variant-meta-video ' +
        'product__media-item--contain';
      cell.setAttribute('data-media-id', mediaId);
      // Clicking a video cell opens the dedicated lightbox (data-yt-id →
      // bindFacadeDelegation → openLightbox). We do NOT use the product modal /
      // swiper for playback because the swiper wrapper's transform makes YouTube
      // render black. NOTE: deliberately NOT using the theme's `.product__media`
      // class here — its `.product__media > *` rule would stretch the play-button
      // <span> to fill the cell (dark overlay = black box).
      cell.innerHTML =
        '<div class="variant-video-grid-opener" data-yt-id="' + video.id + '"' +
             ' role="button" tabindex="0" aria-haspopup="dialog" aria-label="Play video"' +
             ' style="padding-bottom:' + pb + '">' +
          '<img class="variant-video-facade__poster" src="' + youTubePoster(video.id) + '"' +
               ' alt="' + (video.alt || '').replace(/"/g, '&quot;') + '" loading="lazy"' +
               ' style="object-fit:cover;">' +
          '<span class="variant-video-facade__play" aria-hidden="true">' +
            '<svg viewBox="0 0 68 48" width="68" height="48">' +
              '<path class="variant-video-facade__play-bg" d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"></path>' +
              '<path d="M45 24 27 14v20" fill="#fff"></path>' +
            '</svg>' +
          '</span>' +
        '</div>';
      list.appendChild(cell);
    });
  }

  // ── Orchestration ──────────────────────────────────────────────────────────
  function isVariantGrid() {
    return !!document.querySelector('.product__outer--variant-grid');
  }

  function refresh(sectionId, variantId) {
    removeVideoSlides();
    if (!variantId) return;
    // Run after variant-meta-images.js (same tick, setTimeout 0) has rebuilt the
    // gallery, so our slides append after the images.
    setTimeout(function () {
      if (isVariantGrid()) {
        injectVideosGrid(sectionId, variantId);
      } else {
        injectVideosSwiper(sectionId, variantId);
      }
      // Keep the image script's modal thumbnails in sync with the added slides.
      if (window.variantGallery) {
        try {
          window.variantGallery.rebuild();
          window.variantGallery.buildModalThumbs();
        } catch (e) {}
      }
    }, 10);
  }

  // ── Event wiring ───────────────────────────────────────────────────────────
  document.addEventListener('variant:change', function (event) {
    var variant   = event.detail && event.detail.variant;
    var sectionId = event.detail && event.detail.sectionId;
    if (!variant || !sectionId) return;
    if (!hasAnyVideoData(sectionId)) return;
    refresh(sectionId, variant.id);
  });

  document.addEventListener('DOMContentLoaded', function () {
    bindFacadeDelegation();

    // Section id: from variant-radios, or (single-variant variant_grid) the section wrapper.
    var variantRadiosEl = document.querySelector('variant-radios[data-section]');
    var sectionId = variantRadiosEl ? variantRadiosEl.getAttribute('data-section') : null;
    if (!sectionId && isVariantGrid()) {
      var vgOuter = document.querySelector('.product__outer--variant-grid');
      var vgSection = vgOuter && vgOuter.closest('[data-section]');
      sectionId = vgSection ? vgSection.getAttribute('data-section') : null;
    }
    if (!sectionId || !hasAnyVideoData(sectionId)) return;

    // Resolve the initial variant id (URL param → form hidden input → checked radios).
    var variantId = new URLSearchParams(window.location.search).get('variant');
    if (!variantId) {
      var formInput = document.querySelector('#product-form-' + sectionId + ' input[name="id"]');
      if (formInput && formInput.value) variantId = formInput.value;
    }
    if (!variantId) {
      var dataEl = document.querySelector('variant-radios [type="application/json"]');
      if (dataEl) {
        try {
          var variants = JSON.parse(dataEl.textContent);
          var allChecked = Array.from(document.querySelectorAll('variant-radios input:checked'))
            .map(function (r) { return r.value; });
          if (allChecked.length) {
            var found = variants.find(function (v) {
              return allChecked.every(function (opt) { return v.options.indexOf(opt) > -1; });
            });
            if (found) variantId = String(found.id);
          }
        } catch (e) {}
      }
    }
    if (!variantId) return;

    // Wait until the gallery swiper (or grid) is ready, then inject once for the
    // initial variant. Give variant-meta-images.js a moment to filter/inject first.
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      var ready = isVariantGrid()
        ? !!document.querySelector('.product__outer--variant-grid .product__media-list[data-grid-ready]')
        : (function () {
            var el = document.querySelector('.js-media-list');
            return el && el.swiper;
          })();
      if (ready) {
        clearInterval(poll);
        refresh(sectionId, variantId);
      } else if (tries > 100) {           // ~5s safety cap
        clearInterval(poll);
        refresh(sectionId, variantId);
      }
    }, 50);
  });

})();
